package dev.danko.roblox_craft_ai.shared.repository

import dev.danko.roblox_craft_ai.shared.model.AssetResponse
import kotlinx.coroutines.await
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json
import kotlin.js.Promise

private const val DB_NAME = "robloxCraft_v1"
private const val DB_VERSION = 1
private const val STORE_NAME = "assets_by_token"
private const val INDEX_NAME = "tokenHash_cachedAt"
private const val MAX_BYTES = 256 * 1024
private const val TRUNCATED_CODE_MAX_CHARS = 200_000

private val json = Json { ignoreUnknownKeys = true; encodeDefaults = true }

class IndexedDbAssetCacheRepository : AssetCacheRepository {

    private var dbPromise: Promise<dynamic>? = null
    private var indexedDbAvailable = true
    private var fallbackWarned = false
    private val memoryStore: MutableMap<String, MutableList<Pair<Long, AssetResponse>>> = mutableMapOf()

    private fun getTokenList(tokenHash12: String): MutableList<Pair<Long, AssetResponse>> {
        return memoryStore.getOrPut(tokenHash12) { mutableListOf() }
    }

    private fun fallback(m: String) {
        if (!fallbackWarned) {
            fallbackWarned = true
            console.warn("[AssetCache] IndexedDB unavailable, running in-memory mode. Reload will clear history. Reason: $m")
        }
    }

    private fun nowMs(): Long = (js("Date.now()") as Double).toLong()

    private suspend fun openDb(): dynamic {
        val existing = dbPromise
        if (existing != null) {
            return try {
                existing.await()
            } catch (t: Throwable) {
                fallback("db promise failed ${t.message}")
                null
            }
        }
        val p = Promise<dynamic> { resolve: (dynamic) -> Unit, reject: (Throwable) -> Unit ->
            try {
                val win: dynamic = js("window")
                val idb: dynamic = win.indexedDB
                if (idb == null || idb == undefined) {
                    indexedDbAvailable = false
                    reject(Throwable("indexedDB undefined"))
                    return@Promise
                }
                val req: dynamic = idb.open(DB_NAME, DB_VERSION)
                req.onerror = { _: dynamic ->
                    val msg = try { req.error?.message?.toString() ?: "" } catch (_: Throwable) { "" }
                    reject(Throwable("open onerror $msg"))
                }
                req.onupgradeneeded = { _: dynamic ->
                    try {
                        val db: dynamic = req.result
                        val storeNames: dynamic = db.objectStoreNames
                        val hasStore: Boolean = storeNames.contains(STORE_NAME) as Boolean
                        if (!hasStore) {
                            val storeOpts: dynamic = js("({keyPath: 'cacheKey'})")
                            val store: dynamic = db.createObjectStore(STORE_NAME, storeOpts)
                            val keyPathArr: dynamic = js("(['tokenHash12','cachedAtMs'])")
                            val idxOpts: dynamic = js("({unique: false})")
                            store.createIndex(INDEX_NAME, keyPathArr, idxOpts)
                        }
                    } catch (e: Throwable) {
                        console.error("[AssetCache] onupgradeneeded error", e)
                    }
                }
                req.onsuccess = { _: dynamic ->
                    try {
                        val db: dynamic = req.result
                        db.onclose = { _: dynamic -> dbPromise = null }
                        db.onerror = { _: dynamic -> dbPromise = null }
                        resolve(db)
                    } catch (e: Throwable) {
                        reject(e)
                    }
                }
            } catch (t: Throwable) {
                reject(t)
            }
        }
        dbPromise = p
        return try {
            p.await()
        } catch (t: Throwable) {
            indexedDbAvailable = false
            fallback("openDb ${t.message}")
            null
        }
    }

    private fun sizeUtf8(s: String): Int {
        var count = 0
        for (c in s) {
            val code = c.code
            when {
                code < 0x80 -> count += 1
                code < 0x800 -> count += 2
                code in 0xD800..0xDBFF -> count += 4
                else -> count += 3
            }
        }
        return count
    }

    private fun shrinkForPayload(asset: AssetResponse): AssetResponse {
        var a = asset
        var js = json.encodeToString(a)
        if (sizeUtf8(js) <= MAX_BYTES) return a
        val code = a.luauCode
        if (!code.isNullOrBlank() && code.length > TRUNCATED_CODE_MAX_CHARS) {
            a = a.copy(luauCode = code.take(TRUNCATED_CODE_MAX_CHARS))
            js = json.encodeToString(a)
            console.warn("[AssetCache] truncated luauCode for asset=${a.id} to $TRUNCATED_CODE_MAX_CHARS chars")
        }
        if (sizeUtf8(js) > MAX_BYTES && !a.luauCode.isNullOrBlank()) {
            a = a.copy(luauCode = null)
            js = json.encodeToString(a)
            console.warn("[AssetCache] dropped luauCode entirely for asset=${a.id} to fit payload")
        }
        if (sizeUtf8(js) > MAX_BYTES && a.prompt.length > 500) {
            a = a.copy(prompt = a.prompt.take(500))
            js = json.encodeToString(a)
            console.warn("[AssetCache] truncated prompt to 500 chars for asset=${a.id}")
        }
        if (sizeUtf8(js) > MAX_BYTES) {
            a = a.copy(
                payloadUrl = null,
                referenceImages = emptyList(),
                chatHistory = emptyList(),
                revisions = emptyList(),
                files = emptyList(),
                errorMessage = a.errorMessage?.take(200)
            )
            console.error("[AssetCache] aggressive shrink for asset=${a.id}, size still >256KB after all drops")
        }
        return a
    }

    private suspend fun <T> txPromise(block: (resolve: (T) -> Unit, reject: (Throwable) -> Unit) -> Unit): T {
        return Promise<T> { resolve, reject ->
            try {
                block(resolve, reject)
            } catch (t: Throwable) {
                reject(t)
            }
        }.await()
    }

    override suspend fun getRecent(sessionTokenHash12: String, limit: Int): List<AssetResponse> {
        if (!indexedDbAvailable) {
            fallback("idb disabled")
            return getTokenList(sessionTokenHash12)
                .sortedByDescending { it.first }
                .take(limit)
                .map { it.second }
        }
        return try {
            val db: dynamic = openDb() ?: return emptyList()
            val result: MutableList<AssetResponse> = mutableListOf()
            val win: dynamic = js("window")
            txPromise<Unit> { resolve: (Unit) -> Unit, reject: (Throwable) -> Unit ->
                try {
                    val tx: dynamic = db.transaction(arrayOf(STORE_NAME), "readonly")
                    val store: dynamic = tx.objectStore(STORE_NAME)
                    val idx: dynamic = store.index(INDEX_NAME)
                    val lower: dynamic = js("([null, -Infinity])")
                    val upper: dynamic = js("([null, Infinity])")
                    lower[0] = sessionTokenHash12; lower[1] = Double.NEGATIVE_INFINITY
                    upper[0] = sessionTokenHash12; upper[1] = Double.POSITIVE_INFINITY
                    val range: dynamic = win.IDBKeyRange.bound(lower, upper)
                    val req: dynamic = idx.openCursor(range, "prev")
                    var count = 0
                    req.onsuccess = { _: dynamic ->
                        try {
                            val cursor: dynamic = req.result
                            if (cursor == null || cursor == undefined || count >= limit) {
                                resolve(Unit)
                            } else {
                                val row: dynamic = cursor.value
                                val assetJson: String? = row.assetJson as? String
                                if (assetJson != null) {
                                    try {
                                        val asset = json.decodeFromString<AssetResponse>(assetJson)
                                        result.add(asset)
                                        count++
                                    } catch (_: Throwable) {}
                                }
                                cursor["continue"].call(cursor) // `continue` is a Kotlin keyword: call the IDBCursor method by name
                            }
                        } catch (t: Throwable) {
                            reject(t)
                        }
                    }
                    req.onerror = { _: dynamic ->
                        val msg = try { req.error?.message?.toString() ?: "" } catch (_: Throwable) { "" }
                        reject(Throwable("cursor error $msg"))
                    }
                    tx.onerror = { _: dynamic ->
                        val msg = try { tx.error?.message?.toString() ?: "" } catch (_: Throwable) { "" }
                        reject(Throwable("tx error $msg"))
                    }
                } catch (t: Throwable) {
                    reject(t)
                }
            }
            result
        } catch (t: Throwable) {
            fallback("getRecent ${t.message}")
            indexedDbAvailable = false
            emptyList()
        }
    }

    override suspend fun upsert(sessionTokenHash12: String, asset: AssetResponse) {
        if (!indexedDbAvailable) {
            fallback("idb disabled")
            val list = getTokenList(sessionTokenHash12)
            list.removeAll { it.second.id == asset.id }
            list.add(Pair(nowMs(), asset))
            return
        }
        try {
            val shrunk = shrinkForPayload(asset)
            val assetJson = json.encodeToString(shrunk)
            val size = sizeUtf8(assetJson)
            if (size > MAX_BYTES) {
                console.warn("[AssetCache] asset=${shrunk.id} skipped upsert, payload ${size}B > 256KB cap even after shrink")
                return
            }
            val now = nowMs()
            val cacheKey = "${sessionTokenHash12}_${shrunk.id}"
            val db: dynamic = openDb() ?: return
            txPromise<Unit> { resolve: (Unit) -> Unit, reject: (Throwable) -> Unit ->
                try {
                    val tx: dynamic = db.transaction(arrayOf(STORE_NAME), "readwrite")
                    val store: dynamic = tx.objectStore(STORE_NAME)
                    val record: dynamic = js("({})")
                    record.cacheKey = cacheKey
                    record.tokenHash12 = sessionTokenHash12
                    record.assetId = shrunk.id
                    record.cachedAtMs = now.toDouble()
                    record.sizeBytes = size
                    record.assetJson = assetJson
                    val req: dynamic = store.put(record)
                    req.onsuccess = { _: dynamic -> resolve(Unit) }
                    req.onerror = { _: dynamic ->
                        val msg = try { req.error?.message?.toString() ?: "" } catch (_: Throwable) { "" }
                        reject(Throwable("put error $msg"))
                    }
                    tx.oncomplete = { _: dynamic -> resolve(Unit) }
                    tx.onerror = { _: dynamic ->
                        val msg = try { tx.error?.message?.toString() ?: "" } catch (_: Throwable) { "" }
                        reject(Throwable("tx error $msg"))
                    }
                } catch (t: Throwable) {
                    reject(t)
                }
            }
        } catch (t: Throwable) {
            fallback("upsert ${t.message}")
            indexedDbAvailable = false
            val list = getTokenList(sessionTokenHash12)
            list.removeAll { it.second.id == asset.id }
            list.add(Pair(nowMs(), asset))
        }
    }

    override suspend fun delete(sessionTokenHash12: String, assetId: String) {
        if (!indexedDbAvailable) {
            val list = getTokenList(sessionTokenHash12)
            list.removeAll { it.second.id == assetId }
            return
        }
        try {
            val cacheKey = "${sessionTokenHash12}_${assetId}"
            val db: dynamic = openDb() ?: return
            txPromise<Unit> { resolve: (Unit) -> Unit, reject: (Throwable) -> Unit ->
                try {
                    val tx: dynamic = db.transaction(arrayOf(STORE_NAME), "readwrite")
                    val store: dynamic = tx.objectStore(STORE_NAME)
                    val req: dynamic = store.delete(cacheKey)
                    req.onsuccess = { _: dynamic -> resolve(Unit) }
                    req.onerror = { _: dynamic ->
                        val msg = try { req.error?.message?.toString() ?: "" } catch (_: Throwable) { "" }
                        reject(Throwable("delete error $msg"))
                    }
                    tx.oncomplete = { _: dynamic -> resolve(Unit) }
                    tx.onerror = { _: dynamic ->
                        val msg = try { tx.error?.message?.toString() ?: "" } catch (_: Throwable) { "" }
                        reject(Throwable("tx error $msg"))
                    }
                } catch (t: Throwable) {
                    reject(t)
                }
            }
        } catch (t: Throwable) {
            fallback("delete ${t.message}")
            indexedDbAvailable = false
            val list = getTokenList(sessionTokenHash12)
            list.removeAll { it.second.id == assetId }
        }
    }

    override suspend fun clearAll(sessionTokenHash12: String) {
        if (!indexedDbAvailable) {
            memoryStore.remove(sessionTokenHash12)
            return
        }
        try {
            val db: dynamic = openDb() ?: return
            val win: dynamic = js("window")
            val keysToDelete = mutableListOf<String>()
            txPromise<Unit> { resolve: (Unit) -> Unit, reject: (Throwable) -> Unit ->
                try {
                    val tx: dynamic = db.transaction(arrayOf(STORE_NAME), "readonly")
                    val store: dynamic = tx.objectStore(STORE_NAME)
                    val idx: dynamic = store.index(INDEX_NAME)
                    val lower: dynamic = js("([null, -Infinity])")
                    val upper: dynamic = js("([null, Infinity])")
                    lower[0] = sessionTokenHash12; lower[1] = Double.NEGATIVE_INFINITY
                    upper[0] = sessionTokenHash12; upper[1] = Double.POSITIVE_INFINITY
                    val range: dynamic = win.IDBKeyRange.bound(lower, upper)
                    val req: dynamic = idx.openCursor(range)
                    req.onsuccess = { _: dynamic ->
                        try {
                            val cursor: dynamic = req.result
                            if (cursor == null || cursor == undefined) {
                                resolve(Unit)
                            } else {
                                val row: dynamic = cursor.value
                                val k: String? = row.cacheKey as? String
                                if (k != null) keysToDelete.add(k)
                                cursor["continue"].call(cursor)
                            }
                        } catch (t: Throwable) {
                            reject(t)
                        }
                    }
                    req.onerror = { _: dynamic ->
                        val msg = try { req.error?.message?.toString() ?: "" } catch (_: Throwable) { "" }
                        reject(Throwable("cursor error $msg"))
                    }
                    tx.onerror = { _: dynamic ->
                        val msg = try { tx.error?.message?.toString() ?: "" } catch (_: Throwable) { "" }
                        reject(Throwable("tx error $msg"))
                    }
                } catch (t: Throwable) {
                    reject(t)
                }
            }
            if (keysToDelete.isNotEmpty()) {
                txPromise<Unit> { resolve: (Unit) -> Unit, reject: (Throwable) -> Unit ->
                    try {
                        val tx2: dynamic = db.transaction(arrayOf(STORE_NAME), "readwrite")
                        val store2: dynamic = tx2.objectStore(STORE_NAME)
                        keysToDelete.forEach { k ->
                            try { store2.delete(k) } catch (_: Throwable) {}
                        }
                        tx2.oncomplete = { _: dynamic -> resolve(Unit) }
                        tx2.onerror = { _: dynamic ->
                            val msg = try { tx2.error?.message?.toString() ?: "" } catch (_: Throwable) { "" }
                            reject(Throwable("tx2 error $msg"))
                        }
                    } catch (t: Throwable) {
                        reject(t)
                    }
                }
            }
        } catch (t: Throwable) {
            fallback("clearAll ${t.message}")
            indexedDbAvailable = false
            memoryStore.remove(sessionTokenHash12)
        }
    }

    override suspend fun purgeOld(maxAgeMs: Long, maxTotalPerToken: Int) {
        if (!indexedDbAvailable) {
            val now = nowMs()
            val cutoff = now - maxAgeMs
            memoryStore.keys.toList().forEach { token ->
                val list = memoryStore[token] ?: return@forEach
                list.removeAll { it.first < cutoff }
                if (list.size > maxTotalPerToken) {
                    val sorted = list.sortedByDescending { it.first }.take(maxTotalPerToken)
                    list.clear()
                    list.addAll(sorted)
                }
            }
            return
        }
        try {
            val db: dynamic = openDb() ?: return
            val allRows = mutableListOf<dynamic>()
            txPromise<Unit> { resolve: (Unit) -> Unit, reject: (Throwable) -> Unit ->
                try {
                    val tx: dynamic = db.transaction(arrayOf(STORE_NAME), "readonly")
                    val store: dynamic = tx.objectStore(STORE_NAME)
                    val req: dynamic = store.openCursor()
                    req.onsuccess = { _: dynamic ->
                        try {
                            val cursor: dynamic = req.result
                            if (cursor == null || cursor == undefined) {
                                resolve(Unit)
                            } else {
                                allRows.add(cursor.value)
                                cursor["continue"].call(cursor)
                            }
                        } catch (t: Throwable) {
                            reject(t)
                        }
                    }
                    req.onerror = { _: dynamic ->
                        val msg = try { req.error?.message?.toString() ?: "" } catch (_: Throwable) { "" }
                        reject(Throwable("cursor error $msg"))
                    }
                    tx.onerror = { _: dynamic ->
                        val msg = try { tx.error?.message?.toString() ?: "" } catch (_: Throwable) { "" }
                        reject(Throwable("tx error $msg"))
                    }
                } catch (t: Throwable) {
                    reject(t)
                }
            }
            val now = nowMs()
            val cutoff = now - maxAgeMs
            val toDelete = mutableSetOf<String>()
            val perToken = mutableMapOf<String, MutableList<Pair<Long, String>>>()
            for (row in allRows) {
                try {
                    val token: String = (row.tokenHash12 as? String) ?: continue
                    val key: String = (row.cacheKey as? String) ?: continue
                    val cachedAtNum: dynamic = row.cachedAtMs
                    val cachedAt: Long = when {
                        cachedAtNum is Long -> cachedAtNum
                        cachedAtNum is Int -> cachedAtNum.toLong()
                        cachedAtNum is Number -> cachedAtNum.toLong()
                        else -> continue
                    }
                    if (cachedAt < cutoff) {
                        toDelete.add(key)
                        continue
                    }
                    perToken.getOrPut(token) { mutableListOf() }.add(cachedAt to key)
                } catch (_: Throwable) {}
            }
            perToken.values.forEach { list ->
                if (list.size > maxTotalPerToken) {
                    val sorted = list.sortedByDescending { it.first }
                    sorted.drop(maxTotalPerToken).forEach { toDelete.add(it.second) }
                }
            }
            if (toDelete.isNotEmpty()) {
                txPromise<Unit> { resolve: (Unit) -> Unit, reject: (Throwable) -> Unit ->
                    try {
                        val tx2: dynamic = db.transaction(arrayOf(STORE_NAME), "readwrite")
                        val store2: dynamic = tx2.objectStore(STORE_NAME)
                        toDelete.forEach { k ->
                            try { store2.delete(k) } catch (_: Throwable) {}
                        }
                        tx2.oncomplete = { _: dynamic -> resolve(Unit) }
                        tx2.onerror = { _: dynamic ->
                            val msg = try { tx2.error?.message?.toString() ?: "" } catch (_: Throwable) { "" }
                            reject(Throwable("tx2 error $msg"))
                        }
                    } catch (t: Throwable) {
                        reject(t)
                    }
                }
            }
        } catch (t: Throwable) {
            fallback("purgeOld ${t.message}")
            indexedDbAvailable = false
        }
    }
}
